import React from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { Stack } from '@fiverr-private/layout_components';
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@fiverr-private/navigation';
import { VettedProBadge } from '@fiverr-private/badges';
import type { SellerRole } from '../../../gql-operations/gql-generated';
import { useSellerRoles } from '../../hooks/useSellerRoles';
import { useSelectedRoleId, useSelectedRoleIdUpdater } from '../../hooks/useSelectedRoleId';
import { useRoleVariation } from '../../hooks/useRoleVariation';
import { translateRoleTitle } from '../../utils/translateRoleTitle';
import { RolePageTabContent } from '../RolePageTabContent';
import { useReportRoleTabClick } from './useReportRoleTabClick';
import * as styles from './RolePageRolesTabs.ve.css';

const ROLE_ID_PARAM = 'role_id';
const VARIANT_ID_PARAM = 'variant_id';

export const RolePageRolesTabs: React.FC = () => {
    const sellerRoles = useSellerRoles();
    const selectedRoleId = useSelectedRoleId();
    const setSelectedRoleId = useSelectedRoleIdUpdater();
    const selectedRoleTabIndex = useSelectedRoleTabIndex(sellerRoles, selectedRoleId);
    const roleVariation = useRoleVariation();
    const reportTabClick = useReportRoleTabClick();

    const onRoleTabChange = React.useCallback(
        (tabIndex: number) => {
            const urlSearchParams = new URLSearchParams(window.location.search);

            reportTabClick(tabIndex);

            if (tabIndex === 0) {
                urlSearchParams.delete(ROLE_ID_PARAM);
                urlSearchParams.delete(VARIANT_ID_PARAM);
                const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
                window.history.pushState({}, '', newUrl);

                setSelectedRoleId(null);
                return;
            }

            const roleId = sellerRoles[tabIndex - 1]?.id || null;
            if (roleId) {
                const variantId = roleVariation[roleId];

                urlSearchParams.set(ROLE_ID_PARAM, roleId);
                if (variantId) {
                    urlSearchParams.set(VARIANT_ID_PARAM, variantId);
                } else {
                    urlSearchParams.delete(VARIANT_ID_PARAM);
                }
            } else {
                urlSearchParams.delete(ROLE_ID_PARAM);
                urlSearchParams.delete(VARIANT_ID_PARAM);
            }

            const newUrl = `${window.location.pathname}?${urlSearchParams.toString()}`;
            window.history.pushState({}, '', newUrl);

            setSelectedRoleId(roleId);
        },
        [sellerRoles, setSelectedRoleId, roleVariation, reportTabClick]
    );

    return (
        <TabGroup isLazy defaultIndex={selectedRoleTabIndex} onChange={onRoleTabChange}>
            <TabList>
                <Tab fontWeight="normal" color="grey_1200" className={styles.tab}>
                    <I18n k="seller_page_perseus.professions_page.roles_tabs.overview" />
                </Tab>
                {sellerRoles.map((role) => (
                    <Tab key={role.id} fontWeight="normal" color="grey_1200" className={styles.tab}>
                        <Stack gap="2" alignItems="center">
                            {translateRoleTitle(role, roleVariation[role.id])}
                            {role.pro && <VettedProBadge variant="compact" />}
                        </Stack>
                    </Tab>
                ))}
            </TabList>
            <TabPanels>
                <TabPanel padding="0">
                    <RolePageTabContent sellerRole={null} />
                </TabPanel>
                {sellerRoles.map((role) => (
                    <TabPanel key={role.id} padding="0">
                        <RolePageTabContent sellerRole={role} />
                    </TabPanel>
                ))}
            </TabPanels>
        </TabGroup>
    );
};

const useSelectedRoleTabIndex = (sellerRoles: SellerRole[], selectedRoleId: SellerRole['id'] | null): number => {
    if (!selectedRoleId) {
        return 0;
    }
    return sellerRoles.findIndex((role) => role.id === selectedRoleId) + 1;
};
